import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import {
  checkForErrors,
  getPypeConnections,
  toSolutionEnvironment,
  type PlatformConnection,
} from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';

/**
 * Creates a Live Agent Platform connection (a `PypeConnection` row). One
 * mutation — the connection owns the routing values, and no pype is touched
 * because a fresh connection has nothing bound to it yet.
 */
export const createLiveAgentPlatform = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{
  pypeConnections: PlatformConnection[];
  cb?: () => void;
} | void> => {
  if (event.type !== 'createLiveAgentPlatform') {
    return;
  }

  const projectId = ctx.project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  const {
    name,
    platformId,
    agentRouting,
    agentRoutingConfig,
    agentRoutingUrl,
    env,
    cb,
  } = event.payload;

  const response = await urqlGql.createPypeConnection({
    projectId,
    name,
    platformId,
    environment: toSolutionEnvironment(env),
    settings: agentRoutingConfig,
    agentRouting,
    // `null` is deliberate and must survive to the wire: the schema declares
    // `$agentRoutingUrl: String` (nullable) and the backend treats an explicit
    // `null` as "clear this field", while an OMITTED variable means "leave it
    // alone" — measured on main, 2026-09-04. Codegen is configured with
    // `InputMaybe<T> = T`, so its generated variable type says `string |
    // undefined` and cannot express that difference; the cast keeps the null
    // the payload builder decided on rather than collapsing it to `undefined`,
    // which would silently leave a stale callback URL on the connection.
    agentRoutingUrl: agentRoutingUrl as string | undefined,
  });
  checkForErrors(response);

  const pypeConnections = await getPypeConnections({ projectId });

  return { pypeConnections, cb };
};
